import {
  AutocompleteInput,
  type Identifier,
  ReferenceInput,
  useSourceContext,
  useTranslate,
  type Validator,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import { customerName } from '~/helpers/resources/customers'

// todo could use this Input generic
/**
 * A customer picked by name (three letters typed or more). Customers already
 * chosen in the surrounding <ArrayInput>, and `exclude` (the customer whose
 * own relationships these are), are not offered.
 */
interface RelatedCustomerProps {
  source: string
  /** Kept for the call sites' readability; always 'customers'. */
  reference?: 'customers'
  exclude?: Identifier
  validate?: Validator | Validator[]
  label?: string
  autoFocus?: boolean
}

const RelatedCustomer = ({
  exclude,
  reference: _reference,
  autoFocus,
  ...props
}: RelatedCustomerProps) => {
  const translate = useTranslate()
  const values = useWatch()

  // let { data, loading } = useQuery(gql`
  //   {
  //     customers {
  //       id
  //       lastname
  //       firstname
  //     }
  //   }
  // `)
  // data = Object.values(data?.customers || {})

  // `source` is relative inside an <ArrayInput> row; the array name and row
  // index that pick out the already-selected customers live in the full path
  // ('customer_customer.0.related_customer_id'), which the SourceContext holds.
  const path = useSourceContext().getSource(props.source)
  const [, source, index, field] = path.match(/^([^.]+)\.(\d+)\.(.+)$/) ?? []
  // todo after ~10 records selected, values[source] becomes undef.
  const form: any[] = (values ?? {})[source] || []

  const selected = form
    // there are undefined values if not filled
    .filter((_) => !!_)
    // filter out source from form values
    .map((record) => record[field])
    // no customer related to themselves
    .concat(exclude ?? [])

  const escapeRegExp = (value) => (value ? value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '') // $& means the whole matched string

  const getOptionText = (record) => record && customerName(record)

  // The label belongs on the input: react-admin 5 no longer hands
  // ReferenceInput's label down, which left this showing "Customer".
  const label = translate('resources.customers.name', { smart_count: 1 })

  return (
    <ReferenceInput
      source={props.source}
      reference='customers'
      enableGetChoices={({ q }) => (q?.trim().length ?? 0) > 2}
    >
      <AutocompleteInput
        label={label}
        {...props}
        optionText={getOptionText}
        shouldRenderSuggestions={(value) => value.trim().length > 2}
        matchSuggestion={(filter, suggestion) => {
          const regex = new RegExp(escapeRegExp(filter), 'i')
          const suggestionText = getOptionText(suggestion)
          return !selected.includes(suggestion.id) && suggestionText.match(regex)
        }}
        helperText={false}
        TextFieldProps={{ autoFocus }}
      />
    </ReferenceInput>
  )
}

export default RelatedCustomer
